<template>
  <div class="lines-painter">
    <v-stage
      v-if="lineWidth && lineHeight"
      ref="stage"
      :config="{width: lineWidth, height: lineHeight}"
      class="lines-painter__konva"
      @click="createPointOnStage"
    >
      <v-layer>
        <v-circle
          :config="{
            x: this.initialEnterPoint[0].x  || initialPointsEnterPoint[0],
            y: this.initialEnterPoint[0].y  || initialPointsEnterPoint[1],
            stroke: '#000',
            strokeWidth: 1,
            fill: '#10c50f',
            radius: 15,
            opacity: 1,
            dragBoundFunc: getCorrectPointPosition,
            draggable: true
          }"
          @dragmove="dragMoveOnEnterPoint(enterPoint)"
          @dragend="dragEndOnEnterPoint(enterPoint)"
          @mouseout="changeCursor('auto')"
          @mouseover="changeCursor('pointer')"
          @dragstart="dragStartOnPoint()"
        />
      </v-layer>
      <v-layer>
        <!-- Полигон - целая зона с границами между точками. -->
        <v-line
          ref="line"
          :config="{points: polygonPoints, fill: '#b41313', opacity: 3, stroke: '#b41313', strokeWidth: 3, closed: false, draggable: true, dragBoundFunc: getCorrectLinePosition}"
          @dragend="dragEndOnLine()"
          @dragstart="dragStartOnLine()"
          @mouseout="changeCursor('auto')"
          @mouseover="changeCursor('pointer')"
        />
        <!-- Фигура круга - стоит на позиции точки и может перемещаться чтобы редактировать позицию каждой точки. -->
        <template v-if="!isDragging">
          <v-circle
            v-for="(pointConfig, pointIndex) in points"
            :key="pointIndex"
            :config="{
              x: pointConfig.x,
              y: pointConfig.y,
              stroke: '#000',
              strokeWidth: 3,
              shadowEnabled: false,
              fill: '#b41313',
              radius: pointConfig.isDragging ? 9 : 7,
              opacity: pointConfig.isDragging ? 0.9 : 0.6,
              draggable: true,
              dragBoundFunc: getCorrectPointPosition
            }"
            @click="deletePoint($event, pointIndex)"
            @dragend="dragEndOnPoint(pointIndex)"
            @dragmove="dragMoveOnPoint(pointIndex)"
            @dragstart="dragStartOnPoint()"
            @mouseout="changeCursor('auto')"
            @mouseover="changeCursor('pointer')"
          />
        </template>

        <!--          <v-text-->
        <!--            :config="{-->
        <!--              x: 225,-->
        <!--              y: 260,-->
        <!--              text: 'ТОЧКА ВХОДА',-->
        <!--              fontSize: 20,-->
        <!--              fontWeight: 900,-->
        <!--              fill: '#10c50f',-->
        <!--            }"-->
        <!--          />-->
      </v-layer>
    </v-stage>
  </div>
</template>

<script>
/**
 * Компонент для разметки участков (зон) на полотне, фиксированной ширины и высоты.
 * В процессе распознавания видео с камер, системы анализа будут учитывать в своей работы участки кадров видео именно в указанных зонах.
 *
 * Один компонент рисует одну зону. Зона состоит из точек, последовательно соединенных линией.
 * Можно добавлять несколько точек, можно ограничится 2 чтобы отразить просто линию.
 * Точки можно перемещать, чтобы вся зона поменяла вид.
 *
 * Рисование происходит на canvas с помощью библиотеки [Konva.js](https://konvajs.org/).
 *
 * ### Замечание по перемещению
 *
 * В Konva.js перемещение представляет внутри себя изменение смещения всей фигуры относительно первой точки (для круга его центра).
 * На практике это означает отсутствие механизма получить актуальные значения координат точек из компонентов Konva.js,
 * т.к. постоянно надо рассчитывать их путем сложения начальных позиций и смещения. Так и происходит при перемещении всей фигуры (полигона).
 * Однако, этого не происходит при перемещении точек (окружностей), т.к. они изменяются путем удаления старых координат из местной переменной
 * с координатами точек, и последующим добавлением новых координат - т.е. с тчк. зрения Vue происходит пересоздание новых компонентов
 * окружностей с новыми координатами и без всяких смещений.
 */
export default {
  props: {
    /**
     * Ширина полотна для рисования зон.
     */
    lineWidth: {
      type: Number,
      required: true,
    },
    /**
     * Высота полотна для рисования зон.
     */
    lineHeight: {
      type: Number,
      required: true,
    },
    /**
     * Начальное положение точек для зоны.
     */
    initialPoints: {
      type: Array,
    },
    /**
     * Цвет для зоны.
     */
    initialEnterPoint: {
      type: Array,
    },
    initialPointsEnterPoint: {
      type: Array,
      default: () => ([300,300]),
    },
  },
  data() {
    return {
      points: [], // Массив координат и прочих настроек точек зоны.
      isDragging: false, // Флаг для контроля перемещения всей фигуры.
      enterPoint: []
    };
  },
  computed: {
    /**
     * @return {Array} массив координат точек для рисования непрерывной линии по границам зоны.
     */
    polygonPoints() {
      return this.points.map((pointConfig) => ([pointConfig.x, pointConfig.y])).flat();
    },
    /**
     * @return {Number} Положение по Y самой верхней точки фигуры.
     */
    topBound() {
      return _.minBy(this.points, point => point.y).y;
    },
    /**
     * @return {Number} Положение по X самой правой точки фигуры.
     */
    rightBound() {
      return _.maxBy(this.points, point => point.x).x;
    },
    /**
     * @return {Number} Положение по Y самой нижней точки фигуры.
     */
    bottomBound() {
      return _.maxBy(this.points, point => point.y).y;
    },
    /**
     * @return {Number} Положение по X самой левой точки фигуры.
     */
    leftBound() {
      return _.minBy(this.points, point => point.x).x;
    },
  },
  watch: {
    /**
     * Наблюдение за оригинальным значением для его синхронизации со внутренним значением компонента.
     * Приводит к полному пересозданию точек на полотне.
     *
     * @param {Array} newPoints
     */
    initialPoints(newPoints) {
      this.points = [];
      for (const {x, y} of newPoints) {
        this.createPoint(x, y);
      }
    },
    initialEnterPoint(newPoint) {
      this.enterPoint = [];
      for (const {x, y} of newPoint) {
        this.createEnterPoint(x, y);
      }
    },
  },
  /**
   * Парсинг переданных начальных координат по точкам и преобразование их во внутренние объекты.
   */

  created() {
    for (const {x, y} of this.initialPoints) {
      if (this.points.length <= 1) {
        this.createPoint(x, y);
      }
    }
    for (const {x, y} of this.initialEnterPoint) {
      this.createEnterPoint(x, y);
    }
  },
  methods: {
    /**
     * Создаст новую точку зоны на полотне.
     *
     * @param {KonvaEventObject} konvaEvent
     */
    createPointOnStage(konvaEvent) {
      if (konvaEvent.evt.button) {
        return; // Реакция только на основную кнопку мыши.
      }
      const mousePosition = this.$refs.stage.getNode().getPointerPosition();
      this.createPoint(mousePosition.x, mousePosition.y);
      this.savePoints();
    },
    /**
     * Создаст новую точку зоны в заданных координатах.
     * Задано ограничение на максимальное количество точек в зоне.
     *
     * @param {Number} x
     * @param {Number} y
     */
    createPoint(x, y) {
      if (this.points.length <= 1) {
        this.points.push({x, y, isDragging: false});
      }
    },
    createEnterPoint(x, y) {
      this.enterPoint.push({x, y, isDragging: false});
    },
    /**
     * Удаление конкретной точки.
     *
     * @param {KonvaEventObject} konvaEvent
     * @param {Number} pointIndex
     */
    deletePoint(konvaEvent, pointIndex) {
      if (konvaEvent.evt.button) {
        return; // Реакция только на основную кнопку мыши.
      }
      konvaEvent.cancelBubble = true;
      this.points.splice(pointIndex, 1);
      this.savePoints();
    },
    /**
     * Метод срабатывает в момент начала перемещения точки.
     */
    dragStartOnPoint() {
      this.changeCursor("grabbing");
    },
    /**
     * Метод срабатывает в процессе перемещения точки.
     *
     * @param {Number} pointIndex
     */
    dragMoveOnPoint(pointIndex) {
      const mousePosition = this.getCorrectPointPosition(this.$refs.stage.getNode().getPointerPosition());
      this.points.splice(pointIndex, 1, {x: mousePosition.x, y: mousePosition.y, isDragging: true});
    },
    dragMoveOnEnterPoint(enterPoint) {
      const mousePosition = this.getCorrectPointPosition(this.$refs.stage.getNode().getPointerPosition());
      this.enterPoint.splice(enterPoint,  1,{x: mousePosition.x, y: mousePosition.y, isDragging: true});

    },
    /**
     * Метод срабатывает по завершению перемещения точки.
     *
     * @param {Number} pointIndex
     */
    dragEndOnPoint(pointIndex) {
      const mousePosition = this.getCorrectPointPosition(this.$refs.stage.getNode().getPointerPosition());
      this.points.splice(pointIndex, 1, {x: mousePosition.x, y: mousePosition.y, isDragging: false});
      this.changeCursor("auto");
      this.savePoints();
    },
    dragEndOnEnterPoint(enterPoint) {
      const mousePosition = this.getCorrectPointPosition(this.$refs.stage.getNode().getPointerPosition());
      this.enterPoint.splice(enterPoint, 1, {x: mousePosition.x, y: mousePosition.y, isDragging: false});
      // this.points.push(enterPoint,{x: mousePosition.x, y: mousePosition.y, isDragging: false});
      this.savePoints();
    },
    /**
     * Вернет актуальные координаты мыши на полотне для точки.
     * При перемещении устанавливаются границы чтобы не выходить за их пределы.
     *
     * @param {{x: Number, y: Number}} mousePosition Позиция мыши относительно полотна (0,0 в левом верхнем краю).
     * @return {{x: Number, y: Number}}
     */
    getCorrectPointPosition(mousePosition) {
      let x = mousePosition.x,
        y = mousePosition.y;

      if (mousePosition.x < 0) {
        x = 0;
      } else if (mousePosition.x > this.lineWidth) {
        x = this.lineWidth;
      }

      if (mousePosition.y < 0) {
        y = 0;
      } else if (mousePosition.y > this.lineHeight) {
        y = this.lineHeight;
      }

      return {x, y};
    },
    /**
     * Метод срабатывает в момент начала перемещения полигона.
     */
    dragStartOnLine() {
      this.changeCursor("grabbing");
      this.isDragging = true;
    },
    /**
     * Метод срабатывает по завершению перемещения полигона.
     */
    dragEndOnLine() {
      const mouseOffset = this.getCorrectLinePosition({x: this.$refs.line.getNode().x(), y: this.$refs.line.getNode().y()});

      this.points = this.points.map((point) => {
        return {
          ...point,
          x: point.x + mouseOffset.x,
          y: point.y + mouseOffset.y,
        };
      });
      // Зануление внутренних координат, которые меняются после перемещения,
      // чтобы предотвратить расхождение с координатами точек (см. замечание).
      this.$refs.line.getNode().x(0);
      this.$refs.line.getNode().y(0);

      this.changeCursor("pointer");
      this.savePoints();
      this.isDragging = false;
    },
    /**
     * Вернет актуальные координаты при перемещении всей фигуры на полотне.
     * Чтобы фигура не выходила за края полотна.
     *
     * @param {{x: Number, y: Number}} mouseOffset Позиция мыши относительно начала движения фигуры.
     * @return {{x: Number, y: Number}}
     */
    getCorrectLinePosition(mouseOffset) {
      let x = mouseOffset.x,
        y = mouseOffset.y;

      if (this.leftBound + mouseOffset.x < 0) {
        x = -this.leftBound;
      } else if (this.rightBound + mouseOffset.x > this.lineWidth) {
        x = this.lineWidth - this.rightBound;
      }

      if (this.topBound + mouseOffset.y < 0) {
        y = -this.topBound;
      } else if (this.bottomBound + mouseOffset.y > this.lineHeight) {
        y = this.lineHeight - this.bottomBound;
      }

      return {x, y};
    },
    /**
     * Метод для программной смены иконки курсора. Применяется на canvas элементах в виду отсутствия :hover у них.
     *
     * @param {String} cursor
     */
    changeCursor(cursor) {
      document.body.style.cursor = cursor;
    },
    /**
     * Вернет родителю все точки для сохранения.
     */
    savePoints() {
      /**
       * Отдает все координаты точек в виде массива с парами [x, y] когда какая-то точка меняет позицию.
       * @event save-points
       */
      this.$emit("save-points", this.points.map((pointConfig) => ({x: pointConfig.x, y: pointConfig.y})));
      this.$emit("save-enter-point", this.enterPoint.map((pointConfig) => ({x: pointConfig.x, y: pointConfig.y})));

    },
  },
};
</script>

<style lang="scss">
.lines-painter {
  position: absolute;
  z-index: 1;

  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;

  &__konva {
    width: fit-content;
    height: fit-content;
  }
}
</style>
